import { ReactNode, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useTranslation } from '@frontend/i18n';
import { Button, FullscreenModal, Text } from '@frontend/design-system';
import {
  PDF_VIEWER_PADDING,
  documentTextAlternativeStyle,
  modalBodyStyle,
  pageWrapperStyle,
  scrollAreaStyle,
  toolbarActionsStyle,
  toolbarStyle,
  viewerStyle,
  zoomControlsStyle,
  zoomValueStyle,
} from './pdf-field.styles';
import { PdfPageCanvas } from './pdf-page-canvas';
import { PDF_PAGE_BASE_WIDTH, PdfPageSize } from './pdf-placement';
import { useBlockBrowserZoom } from './use-block-browser-zoom';
import { usePdfZoom } from './use-pdf-zoom';

interface PdfViewerProps {
  doc: PDFDocumentProxy;
  pages: PdfPageSize[];
  /** Extra toolbar content, shown after the zoom controls. */
  toolbarActions?: ReactNode;
  /** Content rendered on top of each page, positioned relative to it. */
  renderPageOverlay?: (pageIndex: number) => ReactNode;
  /** Receives each page wrapper so callers can hit-test drops against page rects. */
  pageRef?: (pageIndex: number, element: HTMLDivElement | null) => void;
  onRenderError?: (error: unknown) => void;
}

/** Width available to a page at 100%: the scroll area minus the viewer padding (scrollbar excluded). */
const useAvailableWidth = (element: HTMLElement | null) => {
  const [width, setWidth] = useState(0);

  // Layout effect so the first paint already uses the real width (no oversized flash on phones).
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => setWidth(Math.max(element.clientWidth - PDF_VIEWER_PADDING * 2, 0));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return width;
};

/**
 * Fullscreen-modal document viewer: zoom toolbar plus a scroll area of lazily rendered pages. At 100% a page fits
 * the screen width (capped on desktop); zooming in lets it overflow so it can be panned in both directions.
 * `ref` receives the scroll area.
 */
export const PdfViewer = forwardRef<HTMLDivElement, PdfViewerProps>(
  ({ doc, pages, toolbarActions, renderPageOverlay, pageRef, onRenderError }, ref) => {
    const { t } = useTranslation('digital-forms-experience');
    const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
    const [content, setContent] = useState<HTMLDivElement | null>(null);
    const [documentText, setDocumentText] = useState<{
      doc: PDFDocumentProxy;
      pages: string[];
      isLoading: boolean;
    } | null>(null);
    const pageElements = useRef(new Map<number, HTMLElement>());
    const availableWidth = useAvailableWidth(scrollRoot);
    useBlockBrowserZoom();
    const { zoom, canZoomIn, canZoomOut, zoomIn, zoomOut } = usePdfZoom({ scrollRoot, content, pageElements });
    const pageWidth = Math.round(Math.min(PDF_PAGE_BASE_WIDTH, availableWidth) * zoom);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => scrollRoot, [scrollRoot]);

    useEffect(() => {
      let cancelled = false;
      setDocumentText({ doc, pages: [], isLoading: true });

      const loadText = async () => {
        const pageTexts: string[] = [];
        for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
          if (cancelled) return;
          try {
            const page = await doc.getPage(pageIndex + 1);
            const textContent = await page.getTextContent();
            if (cancelled) return;
            const text = textContent.items
              .reduce((output, item) => {
                if (!('str' in item) || typeof item.str !== 'string') return output;
                return `${output}${item.str}${'hasEOL' in item && item.hasEOL ? '\n' : ' '}`;
              }, '')
              .replace(/[ \t]+\n/g, '\n')
              .replace(/ {2,}/g, ' ')
              .trim();
            pageTexts.push(text);
          } catch {
            // Keep a page-specific fallback in the accessible reading order if extraction fails.
            pageTexts.push('');
          }
        }

        if (!cancelled) setDocumentText({ doc, pages: pageTexts, isLoading: false });
      };

      void loadText();
      return () => {
        cancelled = true;
      };
    }, [doc, pages.length]);

    const documentTextIsLoading = !documentText || documentText.doc !== doc || documentText.isLoading;
    const pageTexts = documentText && documentText.doc === doc ? documentText.pages : [];

    const setPageRef = (pageIndex: number, element: HTMLDivElement | null) => {
      if (element) pageElements.current.set(pageIndex, element);
      else pageElements.current.delete(pageIndex);
      pageRef?.(pageIndex, element);
    };

    return (
      <>
        <div css={toolbarStyle}>
          <div css={zoomControlsStyle} role='toolbar' aria-label={t('Zoom')}>
            <Button
              variant='secondary'
              size='small'
              iconName='zoom-out'
              label={t('Zoom out')}
              disabled={!canZoomOut}
              onClick={zoomOut}
            />
            <Text as='span' size='small' css={zoomValueStyle} aria-live='polite'>
              {`${Math.round(zoom * 100)}%`}
            </Text>
            <Button
              variant='secondary'
              size='small'
              iconName='zoom-in'
              label={t('Zoom in')}
              disabled={!canZoomIn}
              onClick={zoomIn}
            />
          </div>
          {toolbarActions && <div css={toolbarActionsStyle}>{toolbarActions}</div>}
        </div>

        <section
          aria-label={t('Document text alternative')}
          aria-busy={documentTextIsLoading}
          css={documentTextAlternativeStyle}
        >
          {documentTextIsLoading ? (
            <p>{t('Loading document text...')}</p>
          ) : (
            pages.map((_, pageIndex) => (
              <section
                key={pageIndex}
                aria-label={t('Page {{page}} of {{total}}', { page: pageIndex + 1, total: pages.length })}
              >
                <p>{pageTexts[pageIndex] || t('No readable text is available for this page.')}</p>
              </section>
            ))
          )}
        </section>

        <FullscreenModal.Body css={modalBodyStyle}>
          <div ref={setScrollRoot} css={scrollAreaStyle}>
            <div ref={setContent} css={viewerStyle}>
              {pageWidth > 0 &&
                pages.map((size, pageIndex) => (
                  <div
                    key={pageIndex}
                    ref={(element) => setPageRef(pageIndex, element)}
                    data-page-index={pageIndex}
                    css={pageWrapperStyle}
                    style={{ width: pageWidth }}
                  >
                    <PdfPageCanvas
                      doc={doc}
                      pageIndex={pageIndex}
                      pageSize={size}
                      width={pageWidth}
                      scrollRoot={scrollRoot}
                      ariaLabel={t('Page {{page}} of {{total}}', { page: pageIndex + 1, total: pages.length })}
                      onRenderError={onRenderError}
                    />
                    {renderPageOverlay?.(pageIndex)}
                  </div>
                ))}
            </div>
          </div>
        </FullscreenModal.Body>
      </>
    );
  }
);
PdfViewer.displayName = 'PdfViewer';
